package ui.home

import com.essntl.config.EssntlConfig
import com.essntl.core.quickbooks.domain.QuickbooksClient
import core.dateformat.utils.stringToEpochSeconds
import com.essntl.core.utils.repository.RealtimeAction
import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.ViewModel
import com.essntl.core.utils.viewmodel.collectSuccessEvent
import com.essntl.core.utils.viewmodel.getData
import com.essntl.features.authentication.domain.repository.AuthRepository
import com.essntl.features.notification.domain.model.NotificationModel
import com.essntl.features.notification.domain.repository.NotificationRepository
import com.essntl.features.profile.domain.model.ProfileModel
import com.essntl.features.profile.domain.repository.ProfileRepository
import com.essntl.features.profile.domain.use_case.GetCurrentUserUseCase
import com.essntl.features.qbauth.domain.repository.QbAuthRepository
import com.essntl.features.task.domain.model.TaskModel
import com.essntl.features.task.domain.repository.TaskRepository
import com.essntl.features.task.domain.use_case.AddTaskUseCase
import com.essntl.features.task.domain.use_case.DeleteTaskUseCase
import com.essntl.features.task.domain.use_case.UpdateTaskUseCase
import com.essntl.toastmanager.Toast
import com.essntl.toastmanager.ToastManager
import core.config.FeatureConfig
import core.config.buildFlavor
import core.datastore.data.DatastoreManager
import core.datastore.data.DatastoreManager.Companion.FIRST_TIME
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

class HomeViewModel(
    private val authRepository: AuthRepository,
    private val profileRepository: ProfileRepository,
    private val taskRepository: TaskRepository,
    private val qbAuthRepository: QbAuthRepository,
    private val quickbooksClient: QuickbooksClient,
    private val notificationRepository: NotificationRepository,
    private val toastManager: ToastManager,
    private val state: MutableStateFlow<HomeState>,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val datastoreManager: DatastoreManager,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
    addTaskUseCase: AddTaskUseCase,
    updateTaskUseCase: UpdateTaskUseCase,
    deleteTaskUseCase: DeleteTaskUseCase,
) : ViewModel {

    init {
        val firstTime = datastoreManager.getBooleanData(FIRST_TIME)

        if (firstTime) {
            state.update {
                it.copy(
                    openFirstTimeDialog = firstTime,
                )
            }.also {
                datastoreManager.saveBooleanData(FIRST_TIME, false)
            }
        }
    }

    private val todayDate = Clock.System.now()
        .toLocalDateTime(TimeZone.UTC).date.toString()

    init {
        getCurrentProfile()
        getTodayTodoTasks()

        // Add Task Use Case
        collectSuccessEvent(
            flow = addTaskUseCase.successEvent,
            getState = { state.value.getTodayTodoTask },
            setState = { s -> state.update { it.copy(getTodayTodoTask = s) } },
            predicate = { first, second -> first.id == second.id },
            getSetPredicate = { task -> updateTodayTasksPredicate(task) },
        )

        // Edit Task Use Case
        collectSuccessEvent(
            flow = updateTaskUseCase.successEvent,
            getState = { state.value.getTodayTodoTask },
            setState = { s -> state.update { it.copy(getTodayTodoTask = s) } },
            update = { old, new -> new.copy(itineraryService = old.itineraryService) },
            predicate = { first, second -> first.id == second.id },
        )

        // Delete Task Use Case
        collectSuccessEvent(
            flow = deleteTaskUseCase.successEvent,
            getState = { state.value.getTodayTodoTask },
            setState = { s -> state.update { it.copy(getTodayTodoTask = s) } },
            predicate = { first, second -> first.id == second.id },
            getSetPredicate = { task -> updateTodayTasksPredicate(task) },
        )

        // Get Current User Use Case
        collectSuccessEvent(
            flow = getCurrentUserUseCase.successEvent,
            getState = { state.value.getProfile },
            setState = { newState -> state.update { it.copy(getProfile = newState) } },
            onSuccess = { profile ->
                if (profile.appVersion != EssntlConfig.version)
                    updateProfile(
                        profileModel = profile,
                        appVersion = EssntlConfig.version,
                    )
            },
        )
    }

    fun onEvent(event: HomeEvent) {
        when (event) {
            HomeEvent.Logout ->
                logout()

            HomeEvent.CollectNotifications ->
                collectNotifications()

            HomeEvent.CancelCollectNotifications ->
                cancelCollectNotifications()

            HomeEvent.UnsubscribeFromNotifications ->
                unsubscribeFromNotifications()

            HomeEvent.CollectQbAuth ->
                collectQbAuth()

            HomeEvent.UnsubscribeFromQbAuth ->
                unsubscribeFromQbAuth()
        }
    }

    private var collectNotificationsJob: Job? = null
    private fun collectNotifications() {
        if (buildFlavor.isDemo || FeatureConfig.hasWhatsapp == false) return

        println("start listening to notifications")
        collectNotificationsJob?.cancel()
        collectNotificationsJob = scope.launch {
            notificationRepository.collectRealTimeNotifications().onSuccess { flow ->
                flow.collect { action ->
                    println("receiving action: $action")
                    val notification = when (action) {
                        is RealtimeAction.Insert -> action.item
                        is RealtimeAction.Update -> action.item
                        else -> null
                    } ?: return@collect

                    if ((notification.type == NotificationModel.NotificationType.Whatsapp ||
                            notification.type == NotificationModel.NotificationType.WhatsappCall
                            ) && FeatureConfig.hasWhatsapp
                    ) {
                        val message = Toast.Message(
                            // Use conversation id as toast id to avoid multiple toasts for the same conversation
                            id = notification.id,
                            conversationId = "",
                            senderName = notification.title,
                            senderType = null,
                            message = notification.body,
                            type = notification.type?.name,
                            unreadMessages = 0,
                            dateTime = stringToEpochSeconds(notification.createdAt).toString(),
                        )

                        toastManager.message(message = message)
                    }
                }
            }
        }.also {
            it.invokeOnCompletion {
                println("stop listening to notifications")
            }
        }
    }

    private fun cancelCollectNotifications() {
        collectNotificationsJob?.cancel()
    }

    private fun unsubscribeFromNotifications() {
        collectNotificationsJob?.cancel()
        scope.launch {
            notificationRepository.unsubscribeChannel()
        }
    }

    private var collectQbAuthJob: Job? = null
    private fun collectQbAuth() {
        if (buildFlavor.isDemo || FeatureConfig.hasQuickBooks == false) return

        println("start listening to qb auth...")
        collectQbAuthJob?.cancel()
        collectQbAuthJob = scope.launch {
            launch {
                val qbAuth = qbAuthRepository
                    .getAll()
                    .getOrNull()
                    ?.firstOrNull()

                if (qbAuth != null) {
                    println("receiving auth: $qbAuth")

                    quickbooksClient.token.value = qbAuth.accessToken.orEmpty()
                    quickbooksClient.expiresAt.value = qbAuth.expiresAt.orEmpty()
                }
            }

            qbAuthRepository.getFlow().onSuccess {
                it.collect { auth ->
                    println("receiving auth: $auth")

                    quickbooksClient.token.value = auth.accessToken.orEmpty()
                    quickbooksClient.expiresAt.value = auth.expiresAt.orEmpty()
                }
            }
        }.also {
            it.invokeOnCompletion {
                println("stop listening to qb auth...")
            }
        }
    }

    private fun unsubscribeFromQbAuth() {
        collectQbAuthJob?.cancel()
        scope.launch {
            qbAuthRepository.unsubscribe()
        }
    }

    private fun getCurrentProfile() =
        scope.launch {
            getCurrentUserUseCase.get()
        }

    private fun updateProfile(
        profileModel: ProfileModel,
        appVersion: String,
    ) =
        getData(
            getData = { profileRepository.update(profileModel.copy(appVersion = appVersion)) },
            getState = { state.value.getProfile },
            setState = { newState -> state.update { it.copy(getProfile = newState) } },
        )


    private fun logout() =
        scope.launch {
            authRepository.signOut()
        }

    private fun getTodayTodoTasks() {
        getData(
            getData = { taskRepository.getAllTodayTodoTasks(todayDate) },
            getState = { state.value.getTodayTodoTask },
            setState = { newState ->
                state.update {
                    it.copy(getTodayTodoTask = newState)
                }
            },
        )
    }

    private fun updateTodayTasksPredicate(task: TaskModel): Boolean =
        task.dueDate == todayDate &&
            task.status == TaskModel.Status.Todo
}